// src/Component/chat/component/MediaViewer.tsx
import React, { useState, useEffect } from 'react';
import { Icon } from "@iconify/react";

interface MediaViewerProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: 'image' | 'file';
  fileName?: string;
}

const MediaViewer: React.FC<MediaViewerProps> = ({ 
  isOpen, 
  onClose, 
  mediaUrl, 
  mediaType,
  fileName 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
      setIsClosing(false);
    }
  }, [isOpen]);

  if (!isOpen && !isClosing) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Fetch the file as blob
      const response = await fetch(mediaUrl, {
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || getFileNameFromUrl(mediaUrl) || 'download';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      console.log('✅ Download started successfully:', link.download);
    } catch (error) {
      console.error('❌ Download failed, trying fallback method:', error);
      
      // Fallback method - try direct download with anchor tag
      try {
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = fileName || getFileNameFromUrl(mediaUrl) || 'download';
        link.setAttribute('download', link.download);
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        
        console.log('✅ Fallback download triggered');
      } catch (fallbackError) {
        console.error('❌ Fallback download also failed:', fallbackError);
        alert('Download failed. Please try again or right-click and "Save as..."');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const getFileNameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Get filename from path
      let filename = pathname.substring(pathname.lastIndexOf('/') + 1);
      
      // Remove timestamp prefix if exists (e.g., "1234567890_filename.jpg" -> "filename.jpg")
      filename = filename.replace(/^\d+_/, '');
      
      // Decode URL encoding
      filename = decodeURIComponent(filename);
      
      // If no extension found, try to get from content type or default
      if (!filename.includes('.')) {
        filename += '.file';
      }
      
      return filename || 'download';
    } catch {
      return 'download';
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200); // Match animation duration
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-[60] flex items-center justify-center bg-black transition-opacity duration-200 ${
        isClosing ? 'opacity-0 bg-opacity-0' : 'animate-fade-in bg-opacity-75'
      }`}
      onClick={handleBackdropClick}
    >
      {/* Modal Container */}
      <div 
        className={`relative max-w-7xl max-h-[90vh] w-full mx-4 transition-all duration-200 ${
          isClosing ? 'opacity-0 scale-90' : 'animate-scale-in'
        }`}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent rounded-t-lg">
          <div className="flex items-center gap-3 text-white">
            <Icon icon="solar:gallery-bold-duotone" height={24} />
            {fileName && (
              <span className="text-sm font-medium truncate max-w-md">
                {fileName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDownloading ? "Downloading..." : "Download"}
            >
              {isDownloading ? (
                <Icon icon="solar:loading-outline" height={20} className="text-white animate-spin" />
              ) : (
                <Icon icon="solar:download-bold" height={20} className="text-white" />
              )}
            </button> */}
            <button
              onClick={handleClose}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
              title="Close (ESC)"
            >
              <Icon icon="solar:close-circle-bold" height={24} className="text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex items-center justify-center bg-black/50 rounded-lg overflow-hidden min-h-[400px]">
          {mediaType === 'image' ? (
            <div className="relative w-full h-full flex items-center justify-center">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon 
                    icon="solar:loading-outline" 
                    height={48} 
                    className="text-white animate-spin" 
                  />
                </div>
              )}
              {hasError ? (
                <div className="flex flex-col items-center justify-center p-12 text-white">
                  <Icon icon="solar:gallery-broken" height={64} className="mb-4 opacity-50" />
                  <p className="text-lg mb-2">Failed to load image</p>
                  <p className="text-sm opacity-70 mb-4">The image may have been deleted or moved</p>
                  <button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <>
                        <Icon icon="solar:loading-outline" height={18} className="animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Icon icon="solar:download-bold" height={18} />
                        Try downloading instead
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <img
                  src={mediaUrl}
                  alt="Media content"
                  className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                  onLoad={() => setIsLoading(false)}
                  onError={() => {
                    setIsLoading(false);
                    setHasError(true);
                  }}
                  style={{ display: isLoading ? 'none' : 'block' }}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-white">
              <div className="bg-white/10 p-8 rounded-full mb-6">
                <Icon icon="solar:document-text-bold-duotone" height={80} className="opacity-80" />
              </div>
              <p className="text-xl mb-2 font-medium">{fileName || 'File Attachment'}</p>
              <p className="text-sm opacity-70 mb-6">Click download to save and view this file</p>
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="flex items-center gap-2 px-6 py-3 bg-[#00A9A9] hover:bg-[#008888] rounded-lg transition-colors font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <>
                    <Icon icon="solar:loading-outline" height={20} className="animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Icon icon="solar:download-bold" height={20} />
                    Download File
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Bottom gradient for better visibility */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/70 to-transparent pointer-events-none rounded-b-lg" />
      </div>

      {/* Close hint */}
      <div 
        className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/70 text-sm bg-black/30 px-4 py-2 rounded-full backdrop-blur-sm transition-opacity duration-200 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
      >
        Press <kbd className="px-2 py-1 bg-white/20 rounded mx-1">ESC</kbd> or click outside to close
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-in-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MediaViewer;